import { APITypes } from '../../../primary-record-types';
import { SupportCareTeamMetadata } from '../../../primary-record-types/DynamoTypes';
import { SupportCareTeamContactDetailsUpsert } from '../../../primary-record-types/apiTypes/medicalProfileService';
import { AvatarChange } from '../../../primary-record-types/apiTypes/sharedTypes';
import { avatarStateToAvatarChange } from '../../v2BaseComponents/ImagePicker/TempImagePicker';
import { SelectOption } from '../../v2BaseComponents/Inputs/Select/types';
import { HandleUpdateAfterSubmit } from '../ContactDetailsForm';
import { InsuranceFormValue, ProviderFormValue, SupportFormValue } from './types';

export const defaultInsuranceValues: InsuranceFormValue = {
    careTeamMemberType: 'insurance',
    metadata: {
        insuranceProviderName: '',
        primaryCarrierFirstName: '',
        primaryCarrierLastName: '',
        // @ts-expect-error TS2322
        insuranceRole: null,
        groupNumber: '',
        planNumber: '',
        memberId: '',
        // @ts-expect-error TS2322
        expirationDate: null,
        isPreferred: false,
        isActive: false,
    },
    contactDetails: {
        description: '',
        phoneNumber: '',
        faxNumber: '',
        email: '',
        url: '',
        role: '',
        address1: '',
        address2: '',
        entityType: 'care-team-member',
        contactType: 'insurance-care-team-member',
    },
    avatar: null,
    insuranceCardFront: null,
    insuranceCardBack: null,
};

export const defaultSupportValues: SupportFormValue = {
    careTeamMemberType: 'support',
    metadata: {
        isActive: true,
        supportCareTeamMemberType: null,
        otherTypeDescription: null,
    },
    contactDetails: {
        firstName: '',
        lastName: '',
        description: '',
        phoneNumber: '',
        faxNumber: '',
        email: '',
        url: '',
        role: '',
        address1: '',
        address2: '',
        entityType: 'care-team-member',
        contactType: 'support-care-team-member',
    },
    avatar: null,
};

export const defaultProviderValues: ProviderFormValue = {
    careTeamMemberType: 'provider',
    metadata: {
        providerType: null as any,
        providerSpecialty: null as any,
        isActive: true,
    },
    contactDetails: {
        firstName: '',
        lastName: '',
        description: '',
        phoneNumber: '',
        faxNumber: '',
        email: '',
        url: '',
        role: '',
        address1: '',
        address2: '',
        entityType: 'care-team-member',
        contactType: 'provider-care-team-member',
    },
    avatar: null,
};

export const insuranceFormValuesToUpsert = (
    values: InsuranceFormValue,
    initialValues: InsuranceFormValue,
    handleUpdateAfterSubmit: HandleUpdateAfterSubmit,
) =>
    ({
        careTeamMemberType: 'insurance',
        metadata: {
            insuranceProviderName: values.metadata?.insuranceProviderName,
            primaryCarrierFirstName: values.metadata?.primaryCarrierFirstName,
            primaryCarrierLastName: values.metadata?.primaryCarrierLastName,
            insuranceRole: values.metadata?.insuranceRole || undefined,
            groupNumber: values.metadata?.groupNumber,
            planNumber: values.metadata?.planNumber,
            memberId: values.metadata?.memberId,
            expirationDate: values.metadata?.expirationDate || undefined,
            isPreferred: values.metadata?.isPreferred,
            isActive: values.metadata?.isActive,
        },
        avatar: avatarStateToAvatarChange(values.avatar),
        contactDetails: handleUpdateAfterSubmit(initialValues?.contactDetails, values.contactDetails),
        insuranceCardFront: avatarStateToAvatarChange(values.insuranceCardFront),
        insuranceCardBack: avatarStateToAvatarChange(values.insuranceCardBack),
    } as APITypes.medicalProfileService.InsuranceCareTeamMemberDetails<APITypes.medicalProfileService.InsuranceCareTeamContactDetails> & {
        avatar: AvatarChange;
    });

export const supportFormValuesToUpsert = (
    values: SupportFormValue,
    initialValues: SupportFormValue,
    handleUpdateAfterSubmit: HandleUpdateAfterSubmit,
) =>
    ({
        careTeamMemberType: 'support',
        metadata: {
            isActive: values.metadata?.isActive,
            supportCareTeamMemberType: values.metadata?.supportCareTeamMemberType,
            otherTypeDescription: values.metadata?.otherTypeDescription,
        } as SupportCareTeamMetadata,
        contactDetails: handleUpdateAfterSubmit(
            initialValues?.contactDetails,
            values.contactDetails,
        ) as SupportCareTeamContactDetailsUpsert,
        avatar: avatarStateToAvatarChange(values.avatar),
    } as APITypes.medicalProfileService.SupportCareTeamMemberDetails<APITypes.medicalProfileService.SupportCareTeamContactDetails> & {
        avatar: AvatarChange;
    });

export const providerFormValuesToUpsert = (
    values: ProviderFormValue,
    initialValues: ProviderFormValue,
    handleUpdateAfterSubmit: HandleUpdateAfterSubmit,
) =>
    ({
        careTeamMemberType: 'provider',
        metadata: values.metadata,
        avatar: avatarStateToAvatarChange(values.avatar),
        contactDetails: handleUpdateAfterSubmit(initialValues?.contactDetails, values.contactDetails),
    } as APITypes.medicalProfileService.ProviderCareTeamMemberDetails<APITypes.medicalProfileService.ProviderCareTeamContactDetails> & {
        avatar: AvatarChange;
    });

export const isDoctorOrSurgeon = (providerType: string) => {
    return ['Doctor', 'Surgeon'].includes(providerType);
};

export const providerSpecialtyOptions: SelectOption[] = [
    'Interventional Pain Management',
    'Anesthesiology',
    'Addiction Psychiatry',
    'Osteopathic Manipulative Therapy',
    'Adolescent Medicine',
    'Adult Congenital Heart Disease',
    'Otolaryngology',
    'Advanced Heart Failure and Transplant Cardiology',
    'Allergy/Immunology',
    'Pathology',
    'Cardiac Surgery',
    'Cardiology',
    'Pediatric Anesthesiology',
    'Cardiology - Electrophysiology',
    'Child and Adolescent Psychiatry',
    'Pediatric Cardiology',
    'Chiropractic',
    'Colorectal Surgery (Proctologist)',
    'Pediatric Critical Care Medicine',
    'Congenital Cardiac Surgery',
    'Critical Care (Intensivists)',
    'Pediatric Dermatology',
    'Dermatology',
    'Developmental-Behavioral Pediatrics',
    'Pediatric Emergency Medicine',
    'Diagnostic Radiology',
    'Emergency Medicine',
    'Pediatric Endocrinology',
    'Endocrinology',
    'Family Medicine',
    'Pediatric Gastroenterology',
    'Gastroenterology',
    'General Practice',
    'General Surgery',
    'Geriatric Medicine',
    'Geriatric Psychiatry',
    'Gynecological/Oncology',
    'Hand Surgery',
    'Hematology',
    'Hematology/Oncology',
    'Hospice/Palliative Care',
    'Hospitalist',
    'Infectious Disease',
    'Internal Medicine',
    'Interventional Cardiology',
    'Interventional Radiology',
    'Maxillofacial Surgery',
    'Maternal-Fetal Medicine',
    'Medical Genetics and Genomics',
    'Medical Oncology',
    'Medical Toxicologist',
    'Neonatal-Perinatal Medicine',
    'Nephrology',
    'Neurocritcal Care',
    'Neurology',
    'Neurophysiology',
    'Neuropsychiatry',
    'Neuroradiology',
    'Neurosurgery',
    'Neurotology',
    'Nuclear Medicine',
    'Obstetrics/Gynecology',
    'Ophthalmology',
    'Optometry',
    'Oral Surgery (dentists only)',
    'Orthopedic Sports Medicine',
    'Orthopedic Surgery',
    'Pediatric Hematology-Oncology',
    'Pediatric Infectious Disease',
    'Pediatric Medicine',
    'Pediatric Nephrology',
    'Pediatric Pulmonology',
    'Pediatric Radiology',
    'Pediatric Rheumatology',
    'Pediatric Surgery',
    'Pediatric Transplant Hepatology',
    'Pediatric Urology',
    'Pediatric Otolaryngology',
    'Peripheral Vascular Disease',
    'Pharmacy',
    'Physical Medicine and Rehabilitation',
    'Plastic and Reconstructive Surgery',
    'Podiatry',
    'Preventive Medicine',
    'Psychiatry',
    'Pulmonologist',
    'Radiology',
    'Radiology Vascular',
    'Radiation Oncology',
    'Reproductive Endocrinology and Infertility',
    'Rheumatology',
    'Sleep Medicine',
    'Sports Medicine',
    'Surgical Oncology',
    'Thoracic Surgery',
    'Thoracic and Cardiac Surgery',
    'Transplant Hepatology',
    'Undersea and Hyperbaric Medicine',
    'Unknown',
    'Urology',
    'Vascular Neurology',
    'Vascular Surgery',
]
    .sort((a, b) => a.localeCompare(b))
    .map((_) => ({
        label: _,
        value: _,
    }));

export const providerTypeOptions: SelectOption[] = [
    'Chiropractor',
    'Clinic',
    'Community Services',
    'Dentist',
    'Doctor',
    'Hearing',
    'Hospital',
    'Imaging',
    'Lab',
    'Nurse',
    'Other',
    'Pharmacy',
    'Skilled Rehab',
    'Supplier',
    'Surgeon',
    'Therapist (Physical or Mental)',
    'Vision',
    'Wellness & Nutrition',
    'Nurse Practitioner (NP)',
    'Physician Assistant (PA)',
].map((_) => ({
    label: _,
    value: _,
}));

export const supportTypeSelectOptions: SelectOption[] = [
    { value: 'emergency', label: 'Emergency' },
    { value: 'transportation', label: 'Transportation' },
    { value: 'case-manager', label: 'Case Manager' },
    { value: 'aid-or-assistant', label: 'Aid or Assistant' },
    { value: 'family', label: 'Family' },
    { value: 'neighbor', label: 'Neighbor' },
    { value: 'other', label: 'Other' },
];

// Create dynamic support type translations from the options to be used in intl()
export const supportTypeTranslations = supportTypeSelectOptions.reduce((acc, option) => {
    acc[option.value] = option.label;
    return acc;
}, {} as Record<string, string>);
